import React, { useEffect, useRef, useState } from "react";
import { Eraser, X, Undo2, Trash2, Plus, Minus } from "lucide-react";
import { EraseMark } from "../lib/sam2Erase";

interface Props {
  frameUrl: string; // dataURL del frame que se muestra/marca
  frameWidth: number; // ancho real del video (px)
  frameHeight: number;
  // El padre (VideoEditor) sube el frame a Supabase, firma y llama a SAM.
  // Devuelve un dataURL/URL de la máscara para overlay, o lanza.
  onRequestMask: (marks: EraseMark[]) => Promise<string>;
  onConfirm: (marks: EraseMark[]) => void; // dispara el modal de confirmación en el padre
  onClose: () => void;
}

// Pincel inteligente por CLICKS en una MODAL grande (para ver el frame grande).
// El usuario toca DENTRO del objeto (1+ taps) y SAM lo "snapea". NO se traza el
// borde — cada tap es un punto. Tras cada tap se pide el preview automáticamente
// (con guard de carrera). +/- para incluir/excluir. Diseño Atlas.
const EraseBrushPanel: React.FC<Props> = ({
  frameUrl,
  frameWidth,
  frameHeight,
  onRequestMask,
  onConfirm,
  onClose,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  // Overlay verde ya compuesto a partir de la máscara (canvas offscreen).
  const maskOverlayRef = useRef<HTMLCanvasElement | null>(null);
  const reqIdRef = useRef(0); // para descartar previews viejos (carrera)
  const [marks, setMarks] = useState<EraseMark[]>([]);
  const [mode, setMode] = useState<"include" | "exclude">("include");
  const [maskUrl, setMaskUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Render: frame + overlay verde de máscara + puntos.
  const redraw = () => {
    const cv = canvasRef.current,
      img = imgRef.current;
    if (!cv || !img) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.drawImage(img, 0, 0, cv.width, cv.height);
    if (maskOverlayRef.current) {
      ctx.globalAlpha = 0.55;
      ctx.drawImage(maskOverlayRef.current, 0, 0, cv.width, cv.height);
      ctx.globalAlpha = 1;
    }
    const sx = cv.width / frameWidth,
      sy = cv.height / frameHeight;
    for (const m of marks) {
      const p = m.points[0];
      ctx.fillStyle = m.mode === "include" ? "#22c55e" : "#ef4444";
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(p.x * sx, p.y * sy, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
  };

  // Compone un overlay VERDE a partir de la máscara. La máscara puede venir como
  // binaria (objeto blanco sobre negro) o como recorte sobre blanco/transparente;
  // pintamos de verde los píxeles "del objeto" (no-negros y no-transparentes).
  const buildGreenOverlay = (im: HTMLImageElement) => {
    const w = im.naturalWidth || frameWidth;
    const h = im.naturalHeight || frameHeight;
    const off = document.createElement("canvas");
    off.width = w;
    off.height = h;
    const octx = off.getContext("2d");
    if (!octx) return null;
    octx.drawImage(im, 0, 0, w, h);
    let data: ImageData;
    try {
      data = octx.getImageData(0, 0, w, h);
    } catch {
      // canvas tainted (máscara cross-origin sin CORS): no podemos tintar,
      // devolvemos la imagen tal cual.
      return off;
    }
    const px = data.data;
    for (let i = 0; i < px.length; i += 4) {
      const r = px[i],
        g = px[i + 1],
        b = px[i + 2],
        a = px[i + 3];
      // "Pertenece al objeto" = visible y con algo de brillo (no negro puro).
      const lum = (r + g + b) / 3;
      const isObject = a > 20 && lum > 24;
      if (isObject) {
        px[i] = 34; // verde Atlas (#22c55e aprox)
        px[i + 1] = 197;
        px[i + 2] = 94;
        px[i + 3] = 255;
      } else {
        px[i + 3] = 0; // transparente fuera del objeto
      }
    }
    octx.putImageData(data, 0, 0);
    return off;
  };

  // Cargar el overlay de máscara cuando cambia.
  useEffect(() => {
    if (!maskUrl) {
      maskOverlayRef.current = null;
      redraw();
      return;
    }
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => {
      maskOverlayRef.current = buildGreenOverlay(im);
      redraw();
    };
    im.src = maskUrl;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maskUrl]);

  // Cargar el frame.
  useEffect(() => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => {
      imgRef.current = im;
      redraw();
    };
    im.onerror = () => setError("No se pudo cargar el frame del clip.");
    im.src = frameUrl;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameUrl]);

  useEffect(redraw, [marks, frameWidth, frameHeight]); // eslint-disable-line

  // Cerrar con Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Coords del canvas → coords del frame (px reales).
  const toFrame = (e: React.PointerEvent) => {
    const cv = canvasRef.current!;
    const r = cv.getBoundingClientRect();
    const cx = (e.clientX - r.left) * (cv.width / r.width);
    const cy = (e.clientY - r.top) * (cv.height / r.height);
    return { x: (cx / cv.width) * frameWidth, y: (cy / cv.height) * frameHeight };
  };

  // Pide el preview a SAM con las marcas dadas (descarta resultados viejos).
  const preview = async (allMarks: EraseMark[]) => {
    if (!allMarks.length) {
      setMaskUrl(null);
      return;
    }
    const id = ++reqIdRef.current;
    setError(null);
    setLoading(true);
    try {
      const url = await onRequestMask(allMarks);
      if (id === reqIdRef.current) setMaskUrl(url);
    } catch (e: any) {
      if (id === reqIdRef.current)
        setError(e?.message || "No se pudo calcular la selección.");
    } finally {
      if (id === reqIdRef.current) setLoading(false);
    }
  };

  // Un click = un punto adentro del objeto. Auto-preview.
  const onCanvasClick = (e: React.PointerEvent) => {
    const p = toFrame(e);
    const next: EraseMark[] = [...marks, { type: "point", mode, points: [p] }];
    setMarks(next);
    void preview(next);
  };

  const undoLast = () => {
    const next = marks.slice(0, -1);
    setMarks(next);
    void preview(next);
  };

  const clearAll = () => {
    reqIdRef.current++;
    setMarks([]);
    setMaskUrl(null);
    setError(null);
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-brand-black/60 backdrop-blur-sm p-4"
      onPointerDown={(e) => {
        // Click fuera de la card cierra.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex w-full max-w-4xl max-h-[92vh] flex-col overflow-hidden rounded-lg bg-surface-card shadow-md">
        {/* Header */}
        <div className="flex items-center gap-2 border-b border-neutral-200 px-5 py-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-primary/10 text-brand-primary">
            <Eraser size={17} />
          </span>
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold text-brand-black/90 leading-tight">
              Borrar objeto
            </h2>
            <p className="text-[12px] text-neutral-500 leading-tight">
              Tocá <strong>dentro</strong> del objeto a borrar (no lo bordees).
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-auto flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-brand-black/90"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 px-5 py-2.5 border-b border-neutral-200">
          <div className="flex rounded-md border border-neutral-200 p-0.5">
            <button
              onClick={() => setMode("include")}
              className={`flex items-center gap-1 rounded px-2.5 py-1 text-[12px] font-medium transition ${
                mode === "include"
                  ? "bg-brand-primary text-white"
                  : "text-neutral-600 hover:bg-neutral-100"
              }`}
            >
              <Plus size={13} /> Borrar esto
            </button>
            <button
              onClick={() => setMode("exclude")}
              className={`flex items-center gap-1 rounded px-2.5 py-1 text-[12px] font-medium transition ${
                mode === "exclude"
                  ? "bg-red-600 text-white"
                  : "text-neutral-600 hover:bg-neutral-100"
              }`}
            >
              <Minus size={13} /> Conservar
            </button>
          </div>
          <button
            onClick={undoLast}
            disabled={!marks.length}
            className="ml-auto flex items-center gap-1 rounded-md border border-neutral-200 px-2.5 py-1.5 text-[12px] text-neutral-600 hover:bg-neutral-100 disabled:opacity-40"
          >
            <Undo2 size={13} /> Deshacer
          </button>
          <button
            onClick={clearAll}
            disabled={!marks.length}
            className="flex items-center gap-1 rounded-md border border-neutral-200 px-2.5 py-1.5 text-[12px] text-neutral-600 hover:bg-neutral-100 disabled:opacity-40"
          >
            <Trash2 size={13} /> Limpiar
          </button>
        </div>

        {/* Lienzo grande */}
        <div className="flex flex-1 items-center justify-center overflow-auto bg-brand-black/90 p-4">
          <div className="relative inline-block">
            <canvas
              ref={canvasRef}
              width={frameWidth}
              height={frameHeight}
              className="max-h-[60vh] w-auto max-w-full rounded-md touch-none cursor-crosshair shadow-md"
              onPointerDown={onCanvasClick}
            />
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center rounded-md bg-brand-black/30">
                <span className="rounded-md bg-brand-black/70 px-3 py-1.5 text-[13px] text-white">
                  Calculando selección…
                </span>
              </div>
            )}
            {!marks.length && !loading && (
              <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
                <span className="rounded-full bg-brand-black/70 px-3 py-1 text-[12px] text-white">
                  Tocá el objeto para seleccionarlo
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 border-t border-neutral-200 px-5 py-3">
          {error ? (
            <p className="text-[12px] text-red-600 flex-1">{error}</p>
          ) : (
            <p className="text-[12px] text-neutral-500 flex-1">
              Sumá toques si agarró poco, o usá <strong>−</strong> para sacar lo
              que tomó de más.
            </p>
          )}
          <button
            onClick={onClose}
            className="h-9 rounded-md border border-neutral-200 px-4 text-[13px] text-neutral-600 hover:bg-neutral-100"
          >
            Cancelar
          </button>
          <button
            onClick={() => onConfirm(marks)}
            disabled={!maskUrl || loading}
            className="h-9 rounded-md bg-brand-primary px-5 text-[13px] font-medium text-white hover:bg-brand-primary-hover disabled:opacity-40"
          >
            Borrar objeto
          </button>
        </div>
      </div>
    </div>
  );
};

export default EraseBrushPanel;
